import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextField from '../../../components/ui/form/FormTextField'
import FormTextareaField from '../../../components/ui/form/FormTextareaField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { roleFormSchema, type RoleFormValues } from './roleFormSchema'
import type { Role, RoleUpsert } from '../types'

interface Props {
  visible: boolean
  initial: Role | null
  submitting: boolean
  nameError?: string | null
  onClearNameError: () => void
  onSubmit: (payload: RoleUpsert) => void
  onHide: () => void
}

/** Rol ekle/düzenle formu (020: react-hook-form + zod). nameTr zorunlu. */
export default function RoleFormDialog({
  visible,
  initial,
  submitting,
  nameError,
  onClearNameError,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setError } = useZodForm(roleFormSchema, {
    defaultValues: { nameTr: '', nameEn: '', descriptionTr: '', descriptionEn: '' },
  })

  useEffect(() => {
    if (visible)
      reset({
        nameTr: initial?.nameTr ?? '',
        nameEn: initial?.nameEn ?? '',
        descriptionTr: initial?.descriptionTr ?? '',
        descriptionEn: initial?.descriptionEn ?? '',
      })
  }, [visible, initial, reset])

  useEffect(() => {
    if (nameError) {
      setError('nameTr', { type: 'server', message: nameError })
      onClearNameError()
    }
  }, [nameError, setError, onClearNameError])

  const onValid = (v: RoleFormValues) =>
    onSubmit({
      nameTr: v.nameTr,
      nameEn: v.nameEn ? v.nameEn : null,
      descriptionTr: v.descriptionTr ? v.descriptionTr : null,
      descriptionEn: v.descriptionEn ? v.descriptionEn : null,
    })

  return (
    <FormDialog
      visible={visible}
      header={initial ? t('admin.roles.form.editTitle') : t('admin.roles.form.newTitle')}
      submitLabel={t('admin.roles.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <FormTextField control={control} name="nameTr" label={t('admin.roles.form.nameTr')} required autoFocus />
        <FormTextField control={control} name="nameEn" label={t('admin.roles.form.nameEn')} />
        <FormTextareaField control={control} name="descriptionTr" label={t('admin.roles.form.descriptionTr')} rows={2} />
        <FormTextareaField control={control} name="descriptionEn" label={t('admin.roles.form.descriptionEn')} rows={2} />
      </div>
    </FormDialog>
  )
}
